import { stringifyEventProps } from '../stringify';
import { toGroupSuperProperties } from './groupSuperProperties';
import type { EventProps, EventShipper, ShipperGroup, ShipperUser } from './types';

type FaroSession = { attributes?: Record<string, string> };

// Duck-typed Faro API so web (`faro-web-sdk`) and RN (`faro-react-native`) share
// one EventShipper. `initFaro` (web SDK + window) stays out of `core`.
export type FaroApi = {
  pushEvent: (name: string, attributes?: Record<string, string>) => void;
  pushMeasurement?: (
    payload: { type: string; values: Record<string, number> },
    options?: { context?: Record<string, string> }
  ) => void;
  setUser: (user: { id?: string; attributes?: Record<string, string> }) => void;
  resetUser: () => void;
  getSession: () => FaroSession | undefined;
  setSession: (session: FaroSession) => void;
};

export class FaroShipper implements EventShipper {
  readonly name = 'faro';
  private user?: ShipperUser;
  private lastGroupTypes: string[] = [];

  constructor(private faro: { api: FaroApi }) {}

  isReady() {
    return true;
  }

  trackEvent(name: string, props?: EventProps) {
    this.faro.api.pushEvent(name, props ? stringifyEventProps(props) : undefined);
  }

  measure(name: string, value: number, props?: EventProps) {
    this.faro.api.pushMeasurement?.(
      { type: name, values: { [name]: value } },
      props ? { context: stringifyEventProps(props) } : undefined
    );
  }

  identify(user: ShipperUser) {
    this.user = user;
    this.faro.api.setUser({
      id: user.id,
      attributes: { isWorker: String(user.isWorker ?? false) }
    });
  }

  setUserProperty(key: string, value: string | number | boolean) {
    this.faro.api.setUser({
      ...(this.user && { id: this.user.id }),
      attributes: { [key]: String(value) }
    });
  }

  register(props: EventProps) {
    const session = this.faro.api.getSession();
    this.faro.api.setSession({
      ...session,
      attributes: { ...session?.attributes, ...stringifyEventProps(props) }
    });
  }

  unregister(keys: string[]) {
    const session = this.faro.api.getSession();
    const attributes = { ...session?.attributes };
    keys.forEach((key) => {
      delete attributes[key];
    });
    this.faro.api.setSession({ ...session, attributes });
  }

  setGroups(groups: ShipperGroup[]) {
    const nextTypes = [...new Set(groups.map((group) => group.type))];
    const removed = this.lastGroupTypes.filter((type) => !nextTypes.includes(type));
    if (removed.length > 0) {
      this.unregister(removed.flatMap((type) => [`group_${type}`, `group_${type}_id`]));
    }
    this.lastGroupTypes = nextTypes;
    if (groups.length > 0) {
      this.register(toGroupSuperProperties(groups));
    }
  }

  clear() {
    this.user = undefined;
    this.lastGroupTypes = [];
    this.faro.api.resetUser();
    const session = this.faro.api.getSession();
    this.faro.api.setSession({ ...session, attributes: {} });
  }
}
